import {HttpClient, HttpParams} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {Observable, of, throwError} from 'rxjs';
import {environment} from "../../environments/environment";
import {map} from "rxjs/operators";
import {Cheque, ChequeCotizacion, ChequeCotizacionDetail, ChequeParameters, ChequeRequest} from "../models/cheque.model";
import {ChequeCotizacionListFilter} from "../models/cotizacion-list-filter.model";
import {PageableApiResult} from "../models/pagable-api-result.model";
import {ApiResult} from "../shared/model/api-result.model";
import {and, comparison, eq, ge, le, ne} from "rsql-builder";
import {ChequePortfolioFilter} from '../models/cheque-portfolio-filter.model';
import {TransferenciaInfo} from '../models/transferencia-info.model';
import {TransferidoRequest} from '../models/transferido-request.model';

@Injectable({
  providedIn: 'root'
})
export class ChequeApiService {

  private baseUrl:string = environment.apiBaseUrl+"/core/portal/cheques";

  constructor(
    private httpClient: HttpClient
  ) { }

  getChequeList(page, size): Observable<PageableApiResult<Cheque>> {
      return this.httpClient.get<PageableApiResult<Cheque>>(this.baseUrl+`?page=${page}&size=${size}`);
  }

  getChequePropioList(filter: ChequePortfolioFilter): Observable<PageableApiResult<Cheque>> {
    const comps = [];
    if(filter.minimo && filter.maximo) {
      comps.push(comparison("importe", ge(filter.minimo)))
    }
    if(filter.minimo && !filter.maximo) {
      comps.push(comparison("importe", ge(filter.minimo)))
    }
    if(filter.minimo && filter.maximo) {
      comps.push(comparison("importe", le(filter.maximo)))
    }
    if(!filter.minimo && filter.maximo) {
      comps.push(comparison("importe", le(filter.maximo)))
    }
    if(filter.tipoCheque) {
      comps.push(comparison("chequeTipo.id", eq(filter.tipoCheque)))
    }
    if(filter.fromDate && filter.toDate) {
      comps.push(comparison("dueDate", ge(filter.fromDate)))
    }
    if(filter.fromDate && !filter.toDate) {
      comps.push(comparison("dueDate", ge(filter.fromDate)))
    }
    if(filter.fromDate && filter.toDate) {
      comps.push(comparison("dueDate", le(filter.toDate)))
    }
    if(!filter.fromDate && filter.toDate) {
      comps.push(comparison("dueDate", le(filter.toDate)))
    }
    if(filter.estadoCheque){
      comps.push(comparison("chequeStatus.descriptionPyme", eq(filter.estadoCheque)))
    }
    if(filter.librador){
      const libradorValue = filter.librador.replace(/\s/g, '%');
      comps.push('librador==' + '*' + libradorValue + '*');
    }
    let params = new HttpParams();
    if (filter.sortByImporte) {
      params = params.append("rsort", "importe").append("rdirection",filter.sortByImporte);
    }
    if (filter.sortByDueDate) {
      params = params.append("rsort", "dueDate").append("rdirection",filter.sortByDueDate);
    }
    if (comps.length > 0) {
      let q = and(...comps).toString();
      params = params.append("rquery", q);
    }
    return this.httpClient.get<PageableApiResult<Cheque>>(this.baseUrl+`/propios/${filter.page}/${filter.size}`,
      { params });
  }

  getChequeParaInversionList(filter: ChequePortfolioFilter): Observable<PageableApiResult<Cheque>> {
    const comps = [];

    if(filter.minimo && filter.maximo) {
      comps.push(comparison("importe", ge(filter.minimo)))
    }
    if(filter.minimo && !filter.maximo) {
      comps.push(comparison("importe", ge(filter.minimo)))
    }
    if(filter.minimo && filter.maximo) {
      comps.push(comparison("importe", le(filter.maximo)))
    }
    if(!filter.minimo && filter.maximo) {
      comps.push(comparison("importe", le(filter.maximo)))
    }
    if(filter.tipoCheque) {
      comps.push(comparison("chequeTipo.id", eq(filter.tipoCheque)))
    }
    if(filter.fromDate && filter.toDate) {
      comps.push(comparison("dueDate", ge(filter.fromDate)))
    }
    if(filter.fromDate && !filter.toDate) {
      comps.push(comparison("dueDate", ge(filter.fromDate)))
    }
    if(filter.fromDate && filter.toDate) {
      comps.push(comparison("dueDate", le(filter.toDate)))
    }
    if(!filter.fromDate && filter.toDate) {
      comps.push(comparison("dueDate", le(filter.toDate)))
    }
    if(filter.estadoCheque){
      comps.push(comparison("chequeStatus.descriptionPyme", eq(filter.estadoCheque)))
    }
    if(filter.librador){
      const libradorValue = filter.librador.replace(/\s/g, '%');
      comps.push('librador==' + '*' + libradorValue + '*');
    }
    let params = new HttpParams();
    if (filter.sortByImporte) {
      params = params.append("rsort", "importe").append("rdirection",filter.sortByImporte);
    }
    if (filter.sortByDueDate) {
      params = params.append("rsort", "dueDate").append("rdirection",filter.sortByDueDate);
    }
    if (comps.length > 0) {
      let q = and(...comps).toString();
      params = params.append("rquery", q);
    }

    return this.httpClient.get<PageableApiResult<Cheque>>(this.baseUrl+`/inversion/${filter.page}/${filter.size}`,
      { params });
  }

  descontarCheque(cheque:ChequeRequest): Observable<any> {
    return this.httpClient.post<any>(this.baseUrl+'/add',cheque);
  }

  redescontarCheque(chequeId, montoEsperado:number) {
    let cheque = {
      chequeId,
      importeEsperado:montoEsperado
    }
    return this.httpClient.post<any>(this.baseUrl+'/publish/'+chequeId,cheque);
  }

  getCheque(id): Observable<Cheque> {
    return this.httpClient.get<any>(this.baseUrl+`/${id}`);
  }

  getChequeImages(id: number): Observable<any> {
    return this.httpClient.get<any>(`${environment.apiBaseUrl}/core/portal/cheques/images/${id}`);
  }

  getScoreEndosante(chequeId): Observable<any> {
    return this.httpClient.get<any>(this.baseUrl+"/score_endosante/"+chequeId);
  }
  getAdditionalInfoByCuit(cuit: string): Observable<any> {
    return this.httpClient.post<any>(`${environment.apiBaseUrl}/score/VR3`, {documentNumber: cuit});
  }
  searchChequeCotizacionList(filter: ChequeCotizacionListFilter): Observable<PageableApiResult<ChequeCotizacion>> {
    const comps = [];

    let nofilter = true;
    if(filter.dateFilter && filter.dateFilter.from) {
      comps.push(comparison("dueDate", ge(filter.dateFilter.from)))
      nofilter = false;
    }
    if(filter.dateFilter && filter.dateFilter.to) {
      comps.push(comparison("dueDate", le(filter.dateFilter.to)))
      nofilter = false;
    }
    if(filter.minimo) {
      comps.push(comparison("importe", ge(filter.minimo)))
      nofilter = false;
    }
    if(filter.maximo) {
      comps.push(comparison("importe", le(filter.maximo)))
      nofilter = false;
    }
    if(filter.rendimientoMinimo) {
      comps.push(comparison("tnaPorcentajeCalculada", ge(filter.rendimientoMinimo)))
      nofilter = false;
    }
    if(filter.rendimientoMaximo) {
      comps.push(comparison("tnaPorcentajeCalculada", le(filter.rendimientoMaximo)))
      nofilter = false;
    }
    if (filter.scoringLibrador) {
      comps.push(comparison("libradorScore.id", eq(filter.scoringLibrador)));
      nofilter = false;
    }
    if (filter.scoringEndosante) {
      comps.push(comparison("endosanteScorePonderado", eq(filter.scoringEndosante)));
      nofilter = false;
    }
    if (filter.numeroCheque) {
      comps.push(comparison("number", eq(filter.numeroCheque)));
      nofilter = false;
    }
    if (filter.nombreLibrador) {
      comps.push(comparison("librador", eq(filter.nombreLibrador + '*')));
      nofilter = false;
    }
    if (filter.nombreEndosante) {
      comps.push(comparison("endosanteNombre", eq(filter.nombreEndosante + '*')));
      nofilter = false;
    }
    if (filter.conRetiro) {
      comps.push(comparison("retiro", eq(filter.conRetiro)));
      nofilter = false;
    }
    if (filter.tipoCheque) {
      comps.push(comparison("chequeTipoId", eq(filter.tipoCheque)));
      nofilter = false;
    }
    if(nofilter) {
      comps.push(comparison("importe", ge(0)))
    }

    let params:HttpParams = null;
    if (comps.length > 0) {
      let q = and(...comps).toString();
      params = new HttpParams().set("rquery", q);
    }

    if (filter.propio != null) {
      params = params.append('propio', filter.propio)
    }

    if (filter.sortByImporte) {
      params = params.append("rsort", "importe").append("rdirection",filter.sortByImporte);
    }
    if (filter.sortByDueDate) {
      params = params.append("rsort", "dueDate").append("rdirection",filter.sortByDueDate);
    }
    if (filter.sortByMargen) {
      params = params.append("rsort", "margen").append("rdirection",filter.sortByMargen);
    }
    if (filter.sortByTnaClientePorcentaje) {
      params = params.append("rsort", "tnaPorcentajeCalculada").append("rdirection",filter.sortByTnaClientePorcentaje);
    }
    if (filter.sortByValorVenta) {
      params = params.append("rsort", "operacion.valorVenta").append("rdirection",filter.sortByValorVenta);
    }
    if (filter.sortByLibrador) {
      params = params.append("rsort", "librador").append("rdirection",filter.sortByLibrador);
    }


    return this.httpClient.get<PageableApiResult<ChequeCotizacion>>(
      environment.apiBaseUrl +
      `/core/portal/exchange/${filter.page}/${filter.size}`,
      {params}
    );

  }

  getChequeCotizacionById(chequeId: any):Observable<ChequeCotizacionDetail> {
    return this.httpClient.get<ChequeCotizacionDetail>(
      environment.apiBaseUrl +
      `/core/portal/exchange/${chequeId}`,
    );
  }

  getChequeParameters():Observable<ChequeParameters> {
    return this.httpClient.get<ChequeParameters>(
      environment.apiBaseUrl +
      `/core/portal/cheques/parameters`,
    );
  }

  comprarCheque(chequeId: number) {
    return this.httpClient.post<any>(
      environment.apiBaseUrl +
      `/exchange/${chequeId}`,{}
    );
  }

  realizarOfertaPorCheque(body: {chequeId: number, importeEsperado: number, horasVencimiento: number, horasAcreditacion: number}) {
    return this.httpClient.post<any>(`${this.baseUrl}/ofertar/${body.chequeId}`, body);
  }

  getTransacciones() {
    return this.httpClient.get<any>(environment.apiBaseUrl+'/core/portal/profile/transactions');
  }


  cambiarMontoEsperadoCheque(chequeId: number, nuevoEsperado: number):Observable<any> {
    return this.httpClient.put<any>(environment.apiBaseUrl+'/core/portal/cheques/update_valor_solicitado/'+chequeId,{valor:nuevoEsperado});
  }

  quitarPublicacion(chequeId) {
    return this.httpClient.post<any>(this.baseUrl+'/publish/cancel/'+chequeId, {});
  }

  cambiarAceptaOfertaCheque(chequeId: number,aceptaOferta: boolean) {
    return this.httpClient.put<any>(environment.apiBaseUrl+'/core/portal/cheques/update_recibe_ofertas/'+chequeId,{aceptaOferta:aceptaOferta});
  }

  eliminar(chequeId: number) {
    return this.httpClient.delete<any>(this.baseUrl+'/propios/'+chequeId);
  }

  getTransferenciaInfo(idUserTx: number): Observable<TransferenciaInfo> {
    return this.httpClient.get<TransferenciaInfo>(`${this.baseUrl}/datos_transferencia/${idUserTx}`);
  }

  marcarComoTransferido(body: TransferidoRequest): Observable<any> {
    return this.httpClient.put<any>(`${this.baseUrl}/registrar_transferencia`, body);
  }

  searchEcheqById(id: string): Observable<Cheque> {
    return this.httpClient.get<Cheque>(`${environment.apiBaseUrl}/core/portal/echeques/get-echeq?idCheque=${id}`);
  }
}

